import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import supabase from '../supabase/supabase';
import AdminCategoryForm from '../components/AdminCategoryForm';
import AdminPostForm from '../components/AdminPostForm';
import AdminCategoryList from '../components/AdminCategoryList';
import AdminPostList from '../components/AdminPostList';
import AdminBulkAIUpload from '../components/AdminBulkAIUpload';
import AdminApprovals from '../components/AdminApprovals';
import AdminReports from '../components/AdminReports';
import { FiArrowLeft, FiLogOut, FiImage, FiCpu, FiInbox, FiFlag } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const ADMIN_ID = '5ca0e6e1-8cdb-4a6f-89bd-c1846feab27b';

export default function Admin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState('approvals');

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login');
    } else if (session.user.id !== ADMIN_ID) {
      await supabase.auth.signOut();
      navigate('/login');
    } else {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const triggerRefresh = () => setRefreshKey(prev => prev + 1);

  if (loading) return <div className="p-10 text-center font-display text-2xl text-retro-brown">Loading System...</div>;

  return (
    <div className="min-h-screen bg-retro-cream pb-20">
      <header className="bg-retro-teal border-b-4 border-retro-brown px-6 py-4 flex justify-between items-center sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <Link to="/" className="p-2 bg-white border-2 border-retro-brown rounded-full shadow-retro-sm hover:shadow-retro hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-retro-active transition-all text-retro-brown">
            <SafeIcon icon={FiArrowLeft} className="text-xl font-bold" />
          </Link>
          <h1 className="font-display text-3xl tracking-wider text-white drop-shadow-[2px_2px_0_#483122] hidden sm:block">ADMIN ZONE</h1>
        </div>
        <button 
          onClick={handleSignOut}
          className="flex items-center gap-2 bg-white text-retro-brown border-2 border-retro-brown px-4 py-2 rounded-full font-bold shadow-retro-sm hover:shadow-retro hover:-translate-y-0.5 active:translate-y-0.5 active:shadow-retro-active transition-all"
        >
          <SafeIcon icon={FiLogOut} /> EXIT
        </button>
      </header>

      <div className="max-w-7xl mx-auto p-6 mt-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-10 overflow-x-auto pb-4 px-2 hide-scrollbar">
          <button 
            onClick={() => setActiveTab('approvals')}
            className={`px-6 py-3 font-display text-xl tracking-wider transition-all border-4 border-retro-brown rounded-2xl whitespace-nowrap flex items-center gap-3 ${activeTab === 'approvals' ? 'bg-retro-lime text-retro-brown shadow-retro -translate-y-1' : 'bg-white text-retro-brown shadow-retro-sm hover:shadow-retro hover:-translate-y-1 hover:bg-retro-cream'}`}
          >
            <SafeIcon icon={FiInbox} /> Approvals
          </button>
          <button 
            onClick={() => setActiveTab('reports')}
            className={`px-6 py-3 font-display text-xl tracking-wider transition-all border-4 border-retro-brown rounded-2xl whitespace-nowrap flex items-center gap-3 ${activeTab === 'reports' ? 'bg-red-500 text-white shadow-retro -translate-y-1' : 'bg-white text-retro-brown shadow-retro-sm hover:shadow-retro hover:-translate-y-1 hover:bg-retro-cream'}`}
          >
            <SafeIcon icon={FiFlag} /> Reports
          </button>
          <button 
            onClick={() => setActiveTab('content')}
            className={`px-6 py-3 font-display text-xl tracking-wider transition-all border-4 border-retro-brown rounded-2xl whitespace-nowrap flex items-center gap-3 ${activeTab === 'content' ? 'bg-retro-yellow text-retro-brown shadow-retro -translate-y-1' : 'bg-white text-retro-brown shadow-retro-sm hover:shadow-retro hover:-translate-y-1 hover:bg-retro-cream'}`}
          >
            <SafeIcon icon={FiImage} /> Manual Content
          </button>
          <button 
            onClick={() => setActiveTab('bulk-ai')}
            className={`px-6 py-3 font-display text-xl tracking-wider transition-all border-4 border-retro-brown rounded-2xl whitespace-nowrap flex items-center gap-3 ${activeTab === 'bulk-ai' ? 'bg-retro-blue text-white shadow-retro -translate-y-1' : 'bg-white text-retro-brown shadow-retro-sm hover:shadow-retro hover:-translate-y-1 hover:bg-retro-cream'}`}
          >
            <SafeIcon icon={FiCpu} /> Auto AI Upload
          </button>
        </div>

        {activeTab === 'approvals' && (
          <div className="animate-in fade-in duration-300">
            <AdminApprovals refreshKey={refreshKey} onApproveComplete={triggerRefresh} />
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="animate-in fade-in duration-300">
            <AdminReports refreshKey={refreshKey} onReportHandled={triggerRefresh} />
          </div>
        )}

        {activeTab === 'content' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-in fade-in duration-300">
            <div className="lg:col-span-4 space-y-8">
              <AdminCategoryForm onCategoryAdded={triggerRefresh} />
              <AdminCategoryList refreshKey={refreshKey} />
            </div>
            <div className="lg:col-span-8 space-y-8">
              <AdminPostForm onPostUploaded={triggerRefresh} />
              <AdminPostList refreshKey={refreshKey} />
            </div>
          </div>
        )}

        {activeTab === 'bulk-ai' && (
          <div className="grid grid-cols-1 gap-8 animate-in fade-in duration-300">
            <AdminBulkAIUpload onUploadComplete={triggerRefresh} />
            <AdminPostList refreshKey={refreshKey} />
          </div>
        )}

      </div>
    </div>
  );
}